<template>
  <div class="p-6 bg-white shadow rounded space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex flex-wrap items-center gap-3">
        <router-link
          :to="{ name: 'AllStaffInsights' }"
          class="inline-flex items-center gap-1 text-sm font-medium text-white bg-cerulean hover:bg-cerulean/90 px-4 py-2 rounded transition-colors duration-150"
        >
          View All Staff Insights
        </router-link>

      </div>
      <EmployeeDashboardDatePicker embedded />
    </div>

    <!-- Loading / Error states -->
    <div
      v-if="eiStore.isLoading"
      class="text-center py-4"
    >
      Loading staff…
    </div>
    <div
      v-else-if="eiStore.error"
      class="text-red-500"
    >
      {{ eiStore.error }}
    </div>

    <!-- Staff Details + Insights -->
    <div v-else-if="staff">
      <div class="rounded border border-gray-200 bg-gray-50 px-4 py-3">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <select
              id="staffDropdown"
              v-model="selectedId"
              class="min-w-72 rounded border border-gray-300 bg-white px-3 py-2 text-xl font-semibold text-gray-900 shadow-sm focus:border-cerulean focus:ring-cerulean"
              @change="navigateToStaff"
            >
              <option
                v-for="person in eiStore.staffList"
                :key="person.id"
                :value="person.id"
              >
                {{ person.firstname }} {{ person.surname }}
              </option>
            </select>
          </div>

          <div class="flex flex-wrap items-center justify-end gap-3">
            <div class="rounded border border-gray-200 bg-white px-3 py-2">
              <div class="text-xs font-semibold uppercase text-gray-500">
                Billable / Total
              </div>
              <div class="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {{ formatHours(activityTotals.billable) }} / {{ formatHours(activityTotals.total) }}
              </div>
            </div>
            <div class="rounded border border-gray-200 bg-white px-3 py-2">
              <div class="text-xs font-semibold uppercase text-gray-500">
                Assigned Tasks
              </div>
              <div class="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {{ eiStore.tasksList.length }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Task Summary -->
      <div class="mt-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 class="text-lg font-semibold text-gray-800">
            Assigned Tasks
          </h2>

          <div class="flex flex-wrap items-center gap-2">
            <div class="inline-flex rounded border border-gray-300 bg-gray-50 p-0.5 text-xs font-semibold text-gray-600">
              <button
                v-for="option in dueFilterOptions"
                :key="option.value"
                type="button"
                class="rounded px-3 py-1.5 transition-colors"
                :class="dueFilter === option.value ? 'bg-white text-gray-900 shadow-sm' : 'hover:text-gray-900'"
                @click="dueFilter = option.value"
              >
                {{ option.label }}
              </button>
            </div>

            <select
              v-model="taskWindow"
              class="rounded border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
            >
              <option value="next30">
                Next 30 days
              </option>
              <option value="all">
                All due dates
              </option>
            </select>

            <select
              v-model="standingFilter"
              class="rounded border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
            >
              <option value="all">
                Status: All
              </option>
              <option
                v-for="standing in standingOptions"
                :key="standing"
                :value="standing"
              >
                {{ standing }}
              </option>
            </select>

            <select
              v-model="againstTypeFilter"
              class="rounded border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
            >
              <option value="all">
                Type: All
              </option>
              <option
                v-for="type in againstTypeOptions"
                :key="type"
                :value="type"
              >
                {{ formatAgainstType(type) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Task cards -->
        <div class="mt-2 space-y-2">
          <TaskCard
            v-for="task in filteredTasks"
            :key="task.id"
            :task="task"
          />
        </div>
        <div
          v-if="!filteredTasks.length"
          class="text-sm italic text-gray-500"
        >
          No active tasks found for this staff member.
        </div>
      </div>

      <!-- Activities table -->
      <ActivitiesTable />
    </div>

    <!-- No staff selected -->
    <div
      v-else
      class="text-gray-500"
    >
      No staff selected.
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'
import ActivitiesTable from '@/EmployeeInsightsComponents/ActivitiesTable.vue'
import EmployeeDashboardDatePicker from '@/EmployeeInsightsComponents/EmployeeDashboardDatePicker.vue'
import TaskCard from '@/EmployeeInsightsComponents/TaskCard.vue'

const route = useRoute()
const router = useRouter()
const eiStore = useEmployeeInsightsStore()

const selectedId = ref(route.params.staffId || 'me')

// Sync with route param
watch(() => route.params.staffId, async (newId) => {
  selectedId.value = newId || 'me'
  await fetchStaffAndTasks()
})

const staffId = computed(() => selectedId.value)
const staff = computed(() => eiStore.selectedStaff)

const taskWindow = ref('next30')
const dueFilter = ref('all')
const standingFilter = ref('all')
const againstTypeFilter = ref('all')

const dueFilterOptions = [
  { value: 'all', label: 'All' },
  { value: 'dueSoon', label: 'Due soon' },
  { value: 'overdue', label: 'Overdue' }
]

const activityTotals = computed(() => {
  return eiStore.activitiesList.reduce((totals, activity) => {
    const billable = Number(activity.billable || 0)
    const nonBillable = Number(activity.nonBillable || 0)
    totals.billable += billable
    totals.total += billable + nonBillable
    return totals
  }, { billable: 0, total: 0 })
})

const standingOptions = computed(() => {
  return [...new Set(eiStore.tasksList.map(task => task.standing).filter(Boolean))].sort()
})

const againstTypeOptions = computed(() => {
  return [...new Set(eiStore.tasksList.map(task => task.againstType).filter(Boolean))].sort()
})

const filteredTasks = computed(() => {
  const now = Date.now()
  const cutoff = now + 30 * 24 * 60 * 60 * 1000 // 30 days in ms

  return eiStore.tasksList
    .filter(task => {
      if (standingFilter.value !== 'all' && task.standing !== standingFilter.value) return false
      if (againstTypeFilter.value !== 'all' && task.againstType !== againstTypeFilter.value) return false
      if (!task.dateDue) return dueFilter.value === 'all'

      const dueDate = new Date(task.dateDue * 1000)
      const dueTime = dueDate.getTime()
      if (taskWindow.value === 'next30' && dueTime > cutoff) return false
      if (dueFilter.value === 'overdue') return dueTime < startOfToday()
      if (dueFilter.value === 'dueSoon') return dueTime >= startOfToday() && dueTime <= cutoff
      return true
    })
    .sort((a, b) => {
      const aDue = a.dateDue ? new Date(a.dateDue * 1000) : Infinity
      const bDue = b.dateDue ? new Date(b.dateDue * 1000) : Infinity
      return aDue - bDue
    })
})

const navigateToStaff = () => {
  router.push({ name: 'EmployeeDashboard', params: { staffId: selectedId.value } })
}

const startOfToday = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today.getTime()
}

const formatHours = (seconds) => {
  const totalMinutes = Math.round(Number(seconds || 0) / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`
}

const formatAgainstType = (type) => {
  if (!type) return 'Unknown'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

// Fetch function
const fetchStaffAndTasks = async () => {
  await eiStore.fetchStaffById(staffId.value)

  if (eiStore.selectedStaff?.id) {
    if (selectedId.value === 'me') {
      selectedId.value = eiStore.selectedStaff.id
    }
    const filterString = `assignee(${eiStore.selectedStaff.id}),standing(Pending, Accepted, Started, Blocked, Waiting on Customer, Pending Approval)`
    await eiStore.fetchTasks({ filters: filterString })
  }
}

const fetchActivitiesList = async () => {
  if (!eiStore.selectedStaff?.id || !eiStore.startDate || !eiStore.endDate) return

  await eiStore.fetchActivitiesList({
    startDate: eiStore.startDate,
    endDate: eiStore.endDate,
    ownerId: eiStore.selectedStaff.id
  })
}

watch(
  () => [eiStore.startDate, eiStore.endDate, eiStore.selectedStaff?.id],
  () => {
    fetchActivitiesList()
  }
)

// Initial load
onMounted(async () => {
  await eiStore.fetchAllStaff()
  await fetchStaffAndTasks()
})
</script>
